import { useEffect, useRef, useState } from 'react';

export default function WhatWeBuildSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState<number | null>(null);
  const [startX, setStartX] = useState(0);
  const [startProgress, setStartProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || isDragging || dragProgress !== null) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      const windowHeight = window.innerHeight;
      
      // Calculate scroll progress through this section
      const scrollStart = rect.top - windowHeight;
      const scrollEnd = rect.bottom;
      const scrollRange = scrollEnd - scrollStart;
      const currentScroll = -scrollStart;
      
      const progress = Math.max(0, Math.min(1, currentScroll / scrollRange));
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDragging, dragProgress]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setStartProgress(dragProgress !== null ? dragProgress : scrollProgress);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const deltaX = e.clientX - startX;
    const deltaProgress = deltaX / containerWidth;
    
    const newProgress = Math.max(0, Math.min(1, startProgress + deltaProgress));
    setDragProgress(newProgress);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const currentProgress = dragProgress !== null ? dragProgress : scrollProgress;

  return (
    <section ref={sectionRef} className="relative py-32 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-slate-300/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            We Don't Sell You Software.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-teal-500">
              We Build You Custom AI Tools.
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
            There is no one-size-fits-all tool for how your business runs. So we build something that fits exactly — your workflow, your team, your language, your phone.
          </p>
        </div>

        {/* Split-Screen Animation Container */}
        <div 
          ref={containerRef}
          className="relative min-h-[600px] bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200 cursor-ew-resize select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Before State (Left Side) */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-8"
            style={{
              clipPath: `inset(0 ${currentProgress * 100}% 0 0)`,
              transition: isDragging ? 'none' : 'clip-path 0.1s ease-out',
            }}
          >
            <div className="w-full max-w-2xl">
              <div className="mb-8 text-center">
                <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full font-semibold text-sm mb-4">
                  Before: Chaos
                </span>
                <h3 className="text-2xl font-bold text-slate-900">Your Current Reality</h3>
              </div>

              {/* Messy Excel Sheet */}
              <div className="bg-slate-100 rounded-lg p-6 mb-4 border-2 border-slate-300 transform -rotate-2 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-file-excel-2-line text-2xl text-green-600"></i>
                  <span className="font-semibold text-slate-700">Inventory_Final_v3_ACTUAL.xlsx</span>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1 h-8 bg-white border border-slate-300 rounded flex items-center px-2 text-xs text-slate-600">Item A</div>
                    <div className="w-20 h-8 bg-white border border-slate-300 rounded flex items-center px-2 text-xs text-slate-600">???</div>
                    <div className="w-20 h-8 bg-red-100 border border-red-300 rounded flex items-center px-2 text-xs text-red-600 font-semibold">OUT?</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-8 bg-white border border-slate-300 rounded flex items-center px-2 text-xs text-slate-600">Item B</div>
                    <div className="w-20 h-8 bg-yellow-100 border border-yellow-300 rounded flex items-center px-2 text-xs text-yellow-700">Old</div>
                    <div className="w-20 h-8 bg-white border border-slate-300 rounded flex items-center px-2 text-xs text-slate-600">12?</div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-8 bg-white border border-slate-300 rounded flex items-center px-2 text-xs text-slate-600 line-through">Item C</div>
                    <div className="w-20 h-8 bg-white border border-slate-300 rounded"></div>
                    <div className="w-20 h-8 bg-white border border-slate-300 rounded"></div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-500 italic">Last updated: 3 weeks ago?</div>
              </div>

              {/* Scribbled Notebook */}
              <div className="bg-amber-50 rounded-lg p-6 mb-4 border-2 border-amber-200 transform rotate-1 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-book-2-line text-2xl text-amber-700"></i>
                  <span className="font-semibold text-amber-900">Physical Notebook</span>
                </div>
                <div className="space-y-2 font-handwriting">
                  <div className="text-sm text-amber-900 opacity-70">Raj - absent Mon + Tue</div>
                  <div className="text-sm text-amber-900 opacity-70">Order #234 - paid ✓</div>
                  <div className="text-sm text-amber-900 opacity-70 line-through">Call supplier</div>
                  <div className="text-sm text-amber-900 opacity-70">??? check stock room</div>
                </div>
                <div className="mt-3 text-xs text-amber-600 italic">Only one person can read this</div>
              </div>

              {/* WhatsApp Messages */}
              <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200 transform -rotate-1 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-whatsapp-line text-2xl text-green-600"></i>
                  <span className="font-semibold text-green-900">Payment Tracking</span>
                </div>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-2 text-xs text-slate-700 shadow-sm">
                    "Will pay next week" - 2 months ago
                  </div>
                  <div className="bg-white rounded-lg p-2 text-xs text-slate-700 shadow-sm">
                    "Paid 5000" - which invoice???
                  </div>
                  <div className="bg-white rounded-lg p-2 text-xs text-slate-700 shadow-sm">
                    Screenshot of bank transfer (can't find it now)
                  </div>
                </div>
                <div className="mt-3 text-xs text-green-600 italic">Scroll through 500 messages to find payment info</div>
              </div>
            </div>
          </div>

          {/* After State (Right Side) */}
          <div 
            className="absolute inset-0 flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 to-white"
            style={{
              clipPath: `inset(0 0 0 ${(1 - currentProgress) * 100}%)`,
              transition: isDragging ? 'none' : 'clip-path 0.1s ease-out',
            }}
          >
            <div className="w-full max-w-2xl">
              <div className="mb-8 text-center">
                <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full font-semibold text-sm mb-4">
                  After: Clarity
                </span>
                <h3 className="text-2xl font-bold text-slate-900">Your Custom Dashboard</h3>
              </div>

              {/* Clean Dashboard */}
              <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                {/* Dashboard Header */}
                <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className="ri-dashboard-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Business Dashboard</h4>
                      <p className="text-teal-100 text-xs">Real-time updates</p>
                    </div>
                  </div>
                  <div className="text-white text-sm">Today: {new Date().toLocaleDateString()}</div>
                </div>

                {/* Dashboard Content */}
                <div className="p-6 space-y-4">
                  {/* Inventory Status */}
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                          <i className="ri-box-3-line text-teal-600"></i>
                        </div>
                        <span className="font-semibold text-slate-900">Inventory</span>
                      </div>
                      <span className="text-xs text-teal-600 font-semibold">All in stock ✓</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white rounded p-2 text-center border border-slate-200">
                        <div className="text-lg font-bold text-slate-900">45</div>
                        <div className="text-xs text-slate-600">Item A</div>
                      </div>
                      <div className="bg-white rounded p-2 text-center border border-slate-200">
                        <div className="text-lg font-bold text-slate-900">23</div>
                        <div className="text-xs text-slate-600">Item B</div>
                      </div>
                      <div className="bg-white rounded p-2 text-center border border-slate-200">
                        <div className="text-lg font-bold text-slate-900">67</div>
                        <div className="text-xs text-slate-600">Item C</div>
                      </div>
                    </div>
                  </div>

                  {/* Attendance */}
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-cyan-100 rounded-lg flex items-center justify-center">
                          <i className="ri-user-line text-cyan-600"></i>
                        </div>
                        <span className="font-semibold text-slate-900">Team Attendance</span>
                      </div>
                      <span className="text-xs text-cyan-600 font-semibold">8/10 present</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 bg-green-100 rounded p-2 text-center border border-green-200">
                        <div className="text-xs text-green-700 font-semibold">Present: 8</div>
                      </div>
                      <div className="flex-1 bg-red-100 rounded p-2 text-center border border-red-200">
                        <div className="text-xs text-red-700 font-semibold">Absent: 2</div>
                      </div>
                    </div>
                  </div>

                  {/* Payments */}
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                        </div>
                        <span className="font-semibold text-slate-900">Payments</span>
                      </div>
                      <span className="text-xs text-emerald-600 font-semibold">₹45,000 pending</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-white rounded p-2 border border-slate-200">
                        <span className="text-xs text-slate-700">Invoice #234</span>
                        <span className="text-xs font-semibold text-green-600">Paid ✓</span>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded p-2 border border-slate-200">
                        <span className="text-xs text-slate-700">Invoice #235</span>
                        <span className="text-xs font-semibold text-amber-600">Due in 3 days</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dashboard Footer */}
                <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 text-center">
                  <p className="text-xs text-slate-600">
                    <i className="ri-refresh-line mr-1"></i>
                    Auto-updated • Accessible from any device
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Center Divider Line */}
          <div 
            className="absolute top-0 bottom-0 w-1 bg-gradient-to-b from-teal-500 via-teal-600 to-teal-500 shadow-lg z-20 pointer-events-none"
            style={{
              left: `${currentProgress * 100}%`,
              transform: 'translateX(-50%)',
              transition: isDragging ? 'none' : 'left 0.1s ease-out',
            }}
          >
            {/* Animated indicator */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl border-4 border-teal-500 flex items-center justify-center">
              <i className="ri-arrow-left-right-line text-xl text-teal-600"></i>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');
        .font-handwriting {
          font-family: 'Caveat', cursive;
          font-size: 1.1rem;
        }
      `}</style>
    </section>
  );
}